import React, {useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import ChevronRight from '@mui/icons-material/ChevronRight';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import type {IconProps} from '@lightricks/react-design-system/dist/components/icon/IconProps';
import getCountryFullName from '@/utils/getCountryFullName';
import getPlatformDisplayNameFromProvider from '@/utils/getPlatformDisplayNameFromProvider';
import translate from '@/utils/translate';
import {Identity} from '@/types/identity';
import Conditional from '@/components/conditional';
import {TABS} from '@/components/creator-profile/constants';
import useSocialNetworksSelector from '@/components/creator-profile/hooks/use-social-networks-selector';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import DropdownButton from '@/components/dropdown-button';
import {Option} from '@/components/dropdown-button/DropdownButton';
import SocialNetworkIcon from '@/components/social-network-icon';
import StatCard from '@/components/stat-card';
import styles from './MetricsSummaryCard.module.scss';

interface MetricsSummaryCardProps {
  identities: Identity[];
  trackButtonPressed: (buttonName: string, overrideParams?: object) => void;
  fallbackAvatarUrl?: string;
}

interface MetricItem {
  iconName: IconProps['name'];
  value?: string;
  label: string;
}

function HandleWrapper({
  profilePageUrl,
  children,
}: {
  profilePageUrl?: string;
  children: React.ReactNode;
}) {
  if (profilePageUrl) {
    return (
      <a
        className={styles.handleLink}
        href={profilePageUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }
  return <div className={styles.handleInfo}>{children}</div>;
}

function MetricsSummaryCard({
  identities,
  trackButtonPressed,
  fallbackAvatarUrl,
}: MetricsSummaryCardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    socialNetworks,
    activeIdentity: selectedIdentity,
    onSocialNetworkClick,
  } = useSocialNetworksSelector(identities);

  const fallbackIdentity = socialNetworks.length
    ? socialNetworks.reduce((best, id) =>
        id.cachedFollowerCount > best.cachedFollowerCount ? id : best
      )
    : null;

  const activeIdentity = selectedIdentity ?? fallbackIdentity;

  const dropdownOptions: Option[] = socialNetworks.map((network) => ({
    id: network.provider,
    label: getPlatformDisplayNameFromProvider(network.provider),
  }));

  // Declared as a typed variable (rather than chained directly into
  // `.filter()`) so the array literal receives the `MetricItem[]` contextual
  // type and TypeScript can narrow each `iconName` to the strict icon-name
  // union, instead of widening to `string`.
  const allMetrics: MetricItem[] = activeIdentity
    ? [
        {
          iconName: 'Navigation-Profile',
          value: activeIdentity.formattedCachedFollowerCount,
          label: translate('Followers'),
        },
        {
          iconName: 'Features-Pitch-TurnOn',
          value: activeIdentity.formattedAverageEngagementRate,
          label: translate('Eng rate'),
        },
        {
          iconName: 'Features-Eye',
          value: activeIdentity.formattedAverageImpressionsCount,
          label: translate('Avg impressions'),
        },
        // `engagementValue` is the closest available proxy for "average
        // engagements per post" — see ProfilePopup.tsx where the same field
        // is labelled "avg-likes". Backend doesn't yet expose a separate
        // total-engagements field, so we reuse this and adopt the design's
        // label.
        {
          iconName: 'Navigation-Heart-Line',
          value: activeIdentity.formattedEngagementValue,
          label: translate('Avg engagements'),
        },
        // Only meaningful for Instagram; for other platforms this value is
        // empty and the card is filtered out below.
        {
          iconName: 'Social-InstagramStories',
          value: activeIdentity.formattedAverageStoryImpressions,
          label: translate('Story Impressions'),
        },
      ]
    : [];
  const metrics = allMetrics.filter((metric) => !!metric.value);

  const audienceSummary = useMemo(() => {
    const audience = activeIdentity?.audience;
    if (!audience) return null;

    const parts: React.ReactNode[] = [];

    if (audience.topCountry?.country) {
      const countryName = getCountryFullName(audience.topCountry.country);
      parts.push(
        <span key="country">
          {translate('located in')}{' '}
          <strong>
            {countryName} ({audience.topCountry.percentage}%)
          </strong>
        </span>
      );
    }

    if (audience.isMostlyMale && audience.normalizedMaleGenderPercentage) {
      parts.push(
        <span key="gender">
          {translate('is')}{' '}
          <strong>
            {translate('male')} ({audience.normalizedMaleGenderPercentage}%)
          </strong>
        </span>
      );
    } else if (
      audience.isMostlyFemale &&
      audience.normalizedFemaleGenderPercentage
    ) {
      parts.push(
        <span key="gender">
          {translate('is')}{' '}
          <strong>
            {translate('female')} ({audience.normalizedFemaleGenderPercentage}%)
          </strong>
        </span>
      );
    }

    if (audience.topAgeGroup?.ageRange) {
      parts.push(
        <span key="age">
          {translate('is between the ages of')}{' '}
          <strong>
            {audience.topAgeGroup.ageRange} ({audience.topAgeGroup.percentage}%)
          </strong>
        </span>
      );
    }

    if (parts.length === 0) return null;

    return (
      <>
        {translate('Most of the audience is')}{' '}
        {parts.reduce<React.ReactNode[]>((acc, part, i) => {
          if (i === 0) return [part];
          if (i === parts.length - 1) return [...acc, ', and ', part];
          return [...acc, ', ', part];
        }, [])}
      </>
    );
  }, [activeIdentity]);

  const handleSeeAllAnalytics = () => {
    trackButtonPressed('See all analytics');
    const newParams = new URLSearchParams(searchParams);
    newParams.set('activeTabId', TABS.ANALYTICS_AND_AUDIENCE.id);
    if (activeIdentity) {
      newParams.set('activeSocialNetwork', activeIdentity.provider);
    }
    setSearchParams(newParams);
  };

  const handlePlatformChange = (option: Option) => {
    const identity = socialNetworks.find((sn) => sn.provider === option.id);
    if (identity) {
      onSocialNetworkClick(identity);
      trackButtonPressed(`Metrics Summary Platform: ${identity.provider}`);
    }
  };

  if (!activeIdentity) return null;

  return (
    <div
      className={styles.container}
      data-testid={CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__METRICS_SUMMARY}
    >
      <div className={styles.topBar}>
        <DropdownButton
          testID={
            CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__METRICS_SUMMARY__PLATFORM_DROPDOWN
          }
          buttonClassName={styles.platformDropdown}
          options={dropdownOptions}
          selectedOptionIds={[activeIdentity.provider]}
          label={getPlatformDisplayNameFromProvider(activeIdentity.provider)}
          onChange={handlePlatformChange}
          size="large"
          iconSize="small"
          mode="plain"
          appearance="neutral"
          startIcon={
            <SocialNetworkIcon
              name={
                activeIdentity.providerDasherized || activeIdentity.provider
              }
              width={20}
              height={20}
            />
          }
        />
        <HandleWrapper profilePageUrl={activeIdentity.profilePageUrl}>
          <Body size="md" color={designSystemToken('semantic.fg.neutral')}>
            @{activeIdentity.cachedHandle}
          </Body>
          <Conditional
            condition={
              !!(activeIdentity.cachedProfileImageUrl || fallbackAvatarUrl)
            }
          >
            <img
              className={styles.avatar}
              src={activeIdentity.cachedProfileImageUrl || fallbackAvatarUrl}
              alt={activeIdentity.cachedHandle}
            />
          </Conditional>
        </HandleWrapper>
      </div>

      <div className={styles.content}>
        <Headline size="sm">{translate('Metrics')}</Headline>

        <Conditional condition={metrics.length > 0}>
          <div className={styles.metricCardsRow}>
            {metrics.map((metric) => (
              <StatCard
                key={metric.label}
                iconName={metric.iconName}
                value={metric.value}
                label={metric.label}
              />
            ))}
          </div>
        </Conditional>

        <Conditional condition={!!audienceSummary}>
          <div className={styles.audienceCard}>
            <Icon
              name="Navigation-Statistics"
              size="medium"
              appearance="neutral"
            />
            <div className={styles.audienceContent}>
              <Headline size="xs">{translate('Top audience segment')}</Headline>
              <Body size="md">{audienceSummary}</Body>
            </div>
          </div>
        </Conditional>

        <button
          className={styles.seeAllLink}
          onClick={handleSeeAllAnalytics}
          data-testid={
            CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__METRICS_SUMMARY__SEE_ALL_ANALYTICS
          }
          type="button"
        >
          <Label size="md" color={designSystemToken('semantic.fg.primary')}>
            {translate('See all analytics')}
          </Label>
          <ChevronRight className={styles.chevronIcon} />
        </button>
      </div>
    </div>
  );
}

export default MetricsSummaryCard;
